import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { Button } from '../../components/ui.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { HELP_BY_ID, HELP_INDEX, HELP_PAGES } from './catalog.js';
import type { Block, HelpPage, HelpReader, HelpScreen, Inline } from './pages.js';
import { HELP_SECTIONS, HELP_TABS, isForReader } from './pages.js';
import { searchPages } from './search.js';
import './help.css';

/**
 * Help, beside the screen you are on (docs/HELP.md, decision 160).
 *
 * A panel inside the POS rather than a separate site or a new tab, so it knows
 * who is reading and what their store has, and **Take me there** is simply the
 * POS moving to the screen a task starts on — no address to keep, and the same
 * on a desktop, a tablet or a phone. Not a dialog: the steps stay open while
 * you follow them. On a phone it covers the screen, and Take me there closes
 * it so you can see where it took you.
 */

const SCREEN_NAMES: Readonly<Record<HelpScreen, string>> = {
  register: 'Register',
  sales: 'Sales',
  online: 'Online orders',
  customers: 'Customers',
  repairs: 'Repairs',
  stock: 'Inventory',
  reports: 'Reports',
  settings: 'Settings',
};

const GETTING_STARTED = ['start-staff-day-one', 'start-staff-week-one', 'start-new-store'];

function phoneWidth(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches;
}

export function HelpPanel({
  reader,
  currentScreen,
  onTakeMeThere,
  onClose,
}: {
  reader: HelpReader;
  /** Where the POS is now: the panel opens on this screen's tasks. */
  currentScreen: HelpScreen;
  onTakeMeThere: (screen: HelpScreen, tab: string | null) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  /** The pages opened, so Back returns to the one before. */
  const [trail, setTrail] = useState<string[]>([]);
  const heading = useRef<HTMLHeadingElement>(null);

  const visible = useMemo(() => HELP_PAGES.filter((page) => isForReader(page, reader)), [reader]);
  const visibleIds = useMemo(() => new Set(visible.map((page) => page.id)), [visible]);
  const found = useMemo(
    () => searchPages(HELP_INDEX, query, 50).filter((page) => visibleIds.has(page.id)),
    [query, visibleIds],
  );

  const openId = trail.at(-1);
  const open = openId === undefined ? undefined : HELP_BY_ID.get(openId);

  const show = (id: string) => {
    recordBreadcrumb('help', `opened ${id}`);
    setTrail((current) => [...current, id]);
  };

  // A newly opened page is read from its title, by eye and by screen reader.
  useEffect(() => {
    heading.current?.focus();
  }, [openId]);

  // Escape closes, as it closes everything else that floats in this product.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const takeMeThere = (screen: HelpScreen, tab: string | null) => {
    recordBreadcrumb('help', `take me there → ${screen}${tab === null ? '' : ` › ${tab}`}`);
    onTakeMeThere(screen, tab);
    if (phoneWidth()) onClose();
  };

  return (
    <aside className="help-panel" aria-label="Help">
      <header className="help-panel__head">
        {open === undefined ? (
          <h2 className="help-panel__title">Help</h2>
        ) : (
          <Button variant="ghost" onClick={() => setTrail((current) => current.slice(0, -1))}>
            ← Back
          </Button>
        )}
        <Button variant="ghost" onClick={onClose} aria-label="Close help">
          Close
        </Button>
      </header>

      <div className="help-panel__body">
        {open !== undefined ? (
          <PageView
            page={open}
            headingRef={heading}
            currentScreen={currentScreen}
            visibleIds={visibleIds}
            onOpen={show}
            onTakeMeThere={takeMeThere}
          />
        ) : (
          <>
            <label className="help-search">
              <span className="visually-hidden">Search help</span>
              <input
                className="input"
                type="search"
                placeholder="What do you want to do?"
                value={query}
                autoFocus
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>

            {query.trim() !== '' ? (
              found.length === 0 ? (
                <p className="text-secondary help-empty">
                  Nothing matches “{query.trim()}”. Try the word you would say out loud —{' '}
                  <em>refund</em>, <em>count</em>, <em>price</em>.
                </p>
              ) : (
                <PageList pages={found} onOpen={show} label="Results" />
              )
            ) : (
              <>
                <PageList
                  label={`On this screen: ${SCREEN_NAMES[currentScreen]}`}
                  pages={visible.filter(
                    (page) => page.screen === currentScreen && page.section !== 'getting-started',
                  )}
                  onOpen={show}
                />
                <PageList
                  label="Getting started"
                  pages={GETTING_STARTED.filter((id) => visibleIds.has(id)).map((id) =>
                    HELP_BY_ID.get(id)!,
                  )}
                  onOpen={show}
                />
                <section className="help-sections" aria-label="Everything, by section">
                  <h3 className="label-micro">Everything</h3>
                  {HELP_SECTIONS.filter((section) => section.key !== 'getting-started').map(
                    (section) => {
                      const pages = visible.filter((page) => page.section === section.key);
                      if (pages.length === 0) return null;
                      return (
                        <details key={section.key} className="help-section">
                          <summary>
                            {section.label} <span className="text-secondary">({pages.length})</span>
                          </summary>
                          <PageList pages={pages} onOpen={show} />
                        </details>
                      );
                    },
                  )}
                </section>
              </>
            )}
          </>
        )}
      </div>
    </aside>
  );
}

function PageList({
  pages,
  onOpen,
  label,
}: {
  pages: readonly HelpPage[];
  onOpen: (id: string) => void;
  label?: string;
}) {
  if (pages.length === 0) return null;
  return (
    <section className="help-list" aria-label={label}>
      {label === undefined ? null : <h3 className="label-micro">{label}</h3>}
      <ul>
        {pages.map((page) => (
          <li key={page.id}>
            <button type="button" className="help-list__item" onClick={() => onOpen(page.id)}>
              <span className="help-list__title">{page.title}</span>
              <span className="help-list__summary">{page.summary}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PageView({
  page,
  headingRef,
  currentScreen,
  visibleIds,
  onOpen,
  onTakeMeThere,
}: {
  page: HelpPage;
  headingRef: RefObject<HTMLHeadingElement | null>;
  currentScreen: HelpScreen;
  visibleIds: ReadonlySet<string>;
  onOpen: (id: string) => void;
  onTakeMeThere: (screen: HelpScreen, tab: string | null) => void;
}) {
  const guide = page.section === 'getting-started';
  const tabName = page.tab === null ? null : (HELP_TABS[page.screen]?.[page.tab] ?? null);
  return (
    <article className="help-page">
      <h2 className="help-page__title" tabIndex={-1} ref={headingRef}>
        {page.title}
      </h2>
      <p className="help-page__summary">{page.summary}</p>
      {/* A page about one tab keeps its way there even inside its own section:
          on Stock › Products, Count a shelf still needs Stock › Counts (FB-1100). */}
      {guide ? null : currentScreen === page.screen && page.tab === null ? (
        <p className="help-page__here">You are on {SCREEN_NAMES[page.screen]} now.</p>
      ) : (
        <Button variant="primary" onClick={() => onTakeMeThere(page.screen, page.tab)}>
          Take me there — {SCREEN_NAMES[page.screen]}
          {tabName === null ? '' : ` › ${tabName}`}
        </Button>
      )}
      {page.blocks.map((block, index) => (
        <BlockView key={index} block={block} visibleIds={visibleIds} onOpen={onOpen} />
      ))}
    </article>
  );
}

function BlockView({
  block,
  visibleIds,
  onOpen,
}: {
  block: Block;
  visibleIds: ReadonlySet<string>;
  onOpen: (id: string) => void;
}) {
  const render = (parts: readonly Inline[]) => (
    <InlineView parts={parts} visibleIds={visibleIds} onOpen={onOpen} />
  );
  switch (block.kind) {
    case 'heading':
      return <h3 className="help-page__heading">{block.text}</h3>;
    case 'paragraph':
      return <p>{render(block.content)}</p>;
    case 'steps':
      return (
        <ol className="help-page__steps">
          {block.items.map((item, index) => (
            <li key={index}>{render(item)}</li>
          ))}
        </ol>
      );
    case 'list':
      return (
        <ul className="help-page__list">
          {block.items.map((item, index) => (
            <li key={index}>{render(item)}</li>
          ))}
        </ul>
      );
  }
}

function InlineView({
  parts,
  visibleIds,
  onOpen,
}: {
  parts: readonly Inline[];
  visibleIds: ReadonlySet<string>;
  onOpen: (id: string) => void;
}) {
  const out: ReactNode[] = parts.map((part, index) => {
    switch (part.kind) {
      case 'text':
        return part.text;
      case 'bold':
        // An on-screen name, set so it is recognized rather than read.
        return (
          <strong key={index} className="help-ui">
            {part.text}
          </strong>
        );
      case 'italic':
        return <em key={index}>{part.text}</em>;
      case 'link': {
        const target = HELP_BY_ID.get(part.id);
        // A page this person cannot use is named, but not offered.
        if (target === undefined || !visibleIds.has(part.id)) {
          return target === undefined ? null : <span key={index}>{target.title}</span>;
        }
        return (
          <button key={index} type="button" className="link-button" onClick={() => onOpen(part.id)}>
            {target.title}
          </button>
        );
      }
    }
  });
  return <>{out}</>;
}
